/**
 * PRP-WEB-1 W2.8: a delete that can be undone, with no server trash (DD2).
 *
 * `schedule()` hides the row at once (the caller's `onHide`) and shows an Undo toast for
 * {@link UNDO_MS}. The DELETE (`send`) goes out only when the toast expires, when the page is
 * left (`pagehide`), or when this component unmounts (an in-app navigation is leaving the page
 * too). Undo cancels the timer and sends nothing. A second delete while one is pending sends the
 * first at once, so there is only ever one toast. Scheduling the id that is already pending again is
 * a no-op (one DELETE, the same toast). If the send fails, `onFailed` puts the row back.
 *
 * Own ONE instance per page, at the page (the project page holds it, not a tab): a tab switch that
 * unmounts the list must neither send nor drop the toast.
 *
 * Nothing here sends on mount or on a re-render: `send` is only ever called from the timer, the
 * pagehide listener, the unmount cleanup, or a newer `schedule()`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from './ui/button';

/** How long Undo is offered before the DELETE is sent. */
export const UNDO_MS = 10_000;

/** One delete waiting for its toast to run out. */
export interface PendingDelete {
  /** What is being deleted (the parse hash): scheduling the pending id again does nothing. */
  id?: string;
  /** What the toast names ("Kitchen estimate"). */
  label: string;
  /** Sends the DELETE. Called at most once. */
  send: () => Promise<unknown> | unknown;
  /** Undo was pressed: show the row again. */
  onUndo: () => void;
  /** The send failed: show the row again (and say so). */
  onFailed?: (_err: unknown) => void;
}

/** The toast: the deleted thing's name and an Undo button. */
export function UndoDeleteToast({ label, onUndo }: { label: string; onUndo: () => void }) {
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="undo-delete-toast"
      className="fixed bottom-5 left-1/2 z-[60] flex max-w-[calc(100vw-32px)] -translate-x-1/2 items-center gap-3 rounded-[var(--r-lg)] border border-[var(--bd)] bg-[var(--bg-2)] px-4 py-2.5 text-[13px] text-[var(--tx-hi)] shadow-lg"
    >
      <span className="min-w-0 truncate">Deleted {label}.</span>
      <Button variant="secondary" size="sm" onClick={onUndo} data-testid="undo-delete">
        Undo
      </Button>
    </div>
  );
}

/**
 * The delayed-send queue and its toast. Render `toast` once on the page.
 *
 * @returns `schedule` to start a delete, `flush` to send the pending one now (leaving the page), and
 *   `toast`, the element to render (null when idle).
 */
export function useUndoableDelete(): {
  schedule: (_d: PendingDelete) => void;
  flush: () => void;
  toast: JSX.Element | null;
} {
  const pending = useRef<{ d: PendingDelete; timer: ReturnType<typeof setTimeout> } | null>(null);
  const [shown, setShown] = useState<PendingDelete | null>(null);

  const fire = useCallback(() => {
    const p = pending.current;
    if (!p) return;
    clearTimeout(p.timer);
    pending.current = null;
    setShown(null);
    try {
      Promise.resolve(p.d.send()).catch((err) => p.d.onFailed?.(err));
    } catch (err) {
      p.d.onFailed?.(err);
    }
  }, []);

  const schedule = useCallback(
    (d: PendingDelete) => {
      if (d.id !== undefined && pending.current?.d.id === d.id) return; // already pending: one DELETE
      fire(); // an older pending delete goes now; only one toast at a time
      const timer = setTimeout(fire, UNDO_MS);
      pending.current = { d, timer };
      setShown(d);
    },
    [fire],
  );

  const undo = useCallback(() => {
    const p = pending.current;
    if (!p) return;
    clearTimeout(p.timer);
    pending.current = null;
    setShown(null);
    p.d.onUndo();
  }, []);

  useEffect(() => {
    window.addEventListener('pagehide', fire);
    return () => {
      window.removeEventListener('pagehide', fire);
      fire(); // leaving the page in-app sends what the user did not undo
    };
  }, [fire]);

  return { schedule, flush: fire, toast: shown ? <UndoDeleteToast label={shown.label} onUndo={undo} /> : null };
}
